// src/pages/purchase/AwaitingBills.jsx
// D-291 P3d-5a - AWAITING BILLS: goods that were received (store GRN or
// Direct Site finalize) whose vendor bill is not entered yet.
//
// Goods and bill are two events: the GRN put the goods in stock; the vendor's
// bill (entered from here, purchaseBillService.createPurchaseBill) adds what
// we owe to the vendor's khaata. One list, oldest first, one button per row.
//
// Reads only: purchase_receipts where company_id == own AND
// bill_status == "awaiting_bill", ordered by created_at (index in
// firestore.indexes.json). Live (onSnapshot) - when someone enters a bill,
// the row disappears on every open screen.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { canEnterBills } from "../../services/purchaseBillService";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DAY_MS = 24 * 60 * 60 * 1000;
const WAIT_WARN_DAYS = 7;

const toDate = (ts) =>
  ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : null;

export default function AwaitingBills() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const companyId = currentUser?.company_id || "";
  const allowed = canEnterBills(currentUser);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId || !allowed) { setLoading(false); return undefined; }
    const q = query(
      collection(db, "purchase_receipts"),
      where("company_id", "==", companyId),
      where("bill_status", "==", "awaiting_bill"),
      orderBy("created_at", "asc")
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: "estimate" }) })));
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error("[AwaitingBills] load failed:", err);
        setError(err?.code === "failed-precondition"
          ? "This list is still being prepared (database index). Please try again in a few minutes."
          : "Could not load the list. Check your connection and refresh the page.");
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [companyId, allowed]);

  if (!allowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          Only Owner, Accounts, SM, Manager or Store can enter vendor bills.
        </div>
      </div>
    );
  }

  const now = Date.now();
  const totalValue = rows.reduce((sum, r) => sum + Number(r.grand_total || 0), 0);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">Awaiting Bills</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Material received - enter the vendor's bill exactly as printed. The bill adds it to the vendor's khaata.
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading...</p>}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-sm text-green-800">
          Nothing waiting - every received delivery has its bill entered.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className="mb-3 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{rows.length}</span> waiting ·
            received value <span className="font-semibold text-gray-900">₹{inr(totalValue)}</span>
            <span className="text-xs text-gray-400"> (the bill total can differ - GST, freight, round off)</span>
          </div>

          <div className="space-y-3">
            {rows.map((r) => {
              const received = toDate(r.created_at);
              const days = received ? Math.max(0, Math.floor((now - received.getTime()) / DAY_MS)) : null;
              const old = days !== null && days > WAIT_WARN_DAYS;
              const isSite = r.is_direct_site_purchase === true || r.receipt_type === "direct_site_purchase";
              return (
                <div key={r.id} className={`bg-white border rounded-xl p-4 ${old ? "border-amber-300" : "border-gray-200"}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/purchase-orders/${r.purchase_order_id}`)}
                        className="text-sm font-bold text-indigo-700 hover:underline"
                      >
                        {r.po_number || "PO"}
                      </button>
                      <p className="text-sm text-gray-800 font-medium">{r.vendor_name || "-"}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${isSite ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`}>
                          {isSite ? `Direct Site${r.site_name ? ` - ${r.site_name}` : ""}` : "Store GRN"}
                        </span>
                        <span className="text-gray-500">
                          Received {received ? received.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                        </span>
                        {days !== null && (
                          <span className={old ? "text-amber-700 font-semibold" : "text-gray-400"}>
                            {days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} waiting`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <p className="text-[11px] text-gray-400">Received value</p>
                        <p className="text-sm font-semibold text-gray-900">₹{inr(r.grand_total)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/purchase-bills/new/${r.id}`)}
                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap"
                      >
                        Enter bill
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}